import { captureException } from "@sentry/react"
import {
  ErrorComponentProps,
  useLocation,
  useMatches,
  useMatchRoute,
} from "@tanstack/react-router"
import { WelcomeFailReason } from "ipctg-inflow-types/InFlowEnums"
import { useTranslation } from "react-i18next"

import { ExpectedError } from "@/utilities/expectedErrors"
import { FetchError } from "@/utilities/fetch++"
import { scopedTo } from "@/utilities/scopedRoutes"
import { StoreError } from "@/utilities/store"

import { LinkButton } from "../atoms/Button"
import Card from "../atoms/Card"
import Heading from "../atoms/Heading"
import Icon from "../atoms/Icon"
import Text from "../atoms/Text"

interface Props extends ErrorComponentProps {
  resolution: "goHomeHome" | "restartFromWelcome" | "restartViaPAM" | "none"
}

function ErrorComponent(props: Props) {
  const matchRoute = useMatchRoute()
  const matches = useMatches()
  const params = matchRoute({ to: "/qr/$qrLookupID", fuzzy: true })
  const location = useLocation()
  const { error } = props
  const { t } = useTranslation(["errors", "common"])

  const isOnRootQR = matches.some((match) => match.id === "qr/qrLookupID")
  const qrLookupID = (params && params.qrLookupID) || location.search.qrLookupID

  return (
    <div className="error-boundary">
      <Card>
        <Icon size="x-large" name="alert" />
        <div>
          <Heading level={2} as={1} className="center">
            {heading() || t("common.error", { ns: "errors" })}
          </Heading>

          <Text className="center">{message()}</Text>
        </div>
        {/* TODO: clean this up with the error boundary updates - centralize logic out side of the jsx some kind of switch case or sub component would do  */}
        {error instanceof StoreError ? (
          <></>
        ) : !shouldGoHomeHome() && qrLookupID ? (
          <Resolution resolution="restartFromWelcome" qrLookupID={qrLookupID} />
        ) : (
          <Resolution
            resolution={
              props.resolution === "restartFromWelcome"
                ? "goHomeHome"
                : props.resolution
            }
          />
        )}
        <Text className="center" level={2}>
          {t("common.getHelp", { ns: "common" })}{" "}
          <LinkButton
            buttonType="primary"
            presentation="link"
            {...scopedTo("/help", shouldGoHomeHome() ? undefined : qrLookupID)}
            style={{ textDecoration: "underline" }}
          >
            {t("common.clickHere", { ns: "common" })}
          </LinkButton>
        </Text>
      </Card>
    </div>
  )

  function shouldGoHomeHome() {
    const qrErrors = [
      WelcomeFailReason.QRNotFound,
      WelcomeFailReason.QRNotActive,
    ]
    return (
      (error instanceof ExpectedError &&
        qrErrors.some((r) => r === error.failReason)) ||
      (!isOnRootQR && !qrLookupID)
    )
  }

  function heading() {
    if (error instanceof ExpectedError) {
      return
    } else if (error instanceof StoreError) {
      return t("errorBoundary.cookies.header", { ns: "errors" })
    } else {
      return error.name
    }
  }

  function message() {
    if (error instanceof ExpectedError) {
      return t(error.translationKey)
    } else if (error instanceof StoreError) {
      return t("errorBoundary.cookies.message", { ns: "errors" })
    } else if (error instanceof FetchError) {
      switch (error.response.status) {
        case 401:
        case 403:
          return t("common.unauthorized", { ns: "errors" })
        case 404:
          return t("common.notFound", { ns: "errors" })
      }
    }
    captureException(error)
    return error.message
  }
}

type ResolutionProps =
  | {
      resolution: Extract<Props["resolution"], "restartFromWelcome">
      qrLookupID: string
    }
  | {
      resolution: Exclude<Props["resolution"], "restartFromWelcome">
    }

function Resolution(props: ResolutionProps) {
  const { t } = useTranslation(["errors", "common"])
  switch (props.resolution) {
    case "goHomeHome":
      return (
        <LinkButton buttonType="primary" to="/">
          {t("common.goHome", { ns: "common" })}
        </LinkButton>
      )
    case "restartFromWelcome":
      return (
        <LinkButton buttonType="primary" {...scopedTo("/", props.qrLookupID)}>
          {t("common.goHome", { ns: "common" })}
        </LinkButton>
      )
    case "restartViaPAM":
      return (
        <Text className="center">
          {t("errorBoundary.resolution.rescanPAM", { ns: "errors" })}
        </Text>
      )
  }
}

export default ErrorComponent
